import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useAuth } from './AuthContext';
import { AudioVisualizer } from './components/AudioVisualizer';
import { useSentimentWithTranscription } from './useSentimentAnalysis';
import { audioService } from './AudioService';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';


const themeMap = {
  casual: {
    background: 'linear-gradient(to bottom right, #5b21b6, #4c1d95, #1e3a8a)',
    accent: '#a855f7',
    accentSecondary: '#ec4899',
    subtextColor: '#c4b5fd',
    accentRgb: '168, 85, 247',
    waveColors: ['#a855f7', '#ec4899'],
  },
  meditation: {
    background: 'linear-gradient(to bottom right, #4a7c59, #3a6b49, #2d5a3a)',
    accent: '#9DC183',
    accentSecondary: '#5f9ea0',
    subtextColor: '#b8d8ba',
    accentRgb: '157, 193, 131',
    waveColors: ['#48C9B0', '#5DADE2'],
  },
  therapy: {
    background: 'linear-gradient(to bottom right, #7b68a8, #6a5a96, #5a4d82)',
    accent: '#B4A7D6',
    accentSecondary: '#9DC183',
    subtextColor: '#d0c4e8',
    accentRgb: '180, 167, 214',
    waveColors: ['#A569BD', '#D2B4DE'],
  },
  boardgames: {
    background: 'linear-gradient(to bottom right, #b8860b, #a0750a, #8b6508)',
    accent: '#FFB347',
    accentSecondary: '#FF8C00',
    subtextColor: '#ffe0a6',
    accentRgb: '255, 179, 71',
    waveColors: ['#F5B041', '#F39C12'],
  },
  dnd: {
    background: 'linear-gradient(to bottom right, #8B0000, #6b0000, #4a0000)',
    accent: '#FFD700',
    accentSecondary: '#B22222',
    subtextColor: '#ffecb3',
    accentRgb: '255, 215, 0',
    waveColors: ['#F4D03F', '#F39C12'],
  },
};

const contextModes = [
  { id: 'casual', label: 'Casual Conversation', emoji: '💬' },
  { id: 'meditation', label: 'Meditation', emoji: '🧘' },
  { id: 'therapy', label: 'Therapy', emoji: '🩺' },
  { id: 'boardgames', label: 'Board Games', emoji: '🎲' },
  { id: 'dnd', label: 'D&D Adventure', emoji: '🐉' },
];

function Dashboard() {
  const { user, logout } = useAuth();
  const [isListening, setIsListening] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(70);
  const [currentMood, setCurrentMood] = useState(null);
  const [selectedMode, setSelectedMode] = useState('casual');

  const theme = themeMap[selectedMode];

  // Transcription state
  const [transcript, setTranscript] = useState('');
  const [interimTranscript, setInterimTranscript] = useState('');
  const recognitionRef = useRef(null);
  const shouldListenRef = useRef(false);

  // Sentiment analysis
  const sentiment = useSentimentWithTranscription(transcript + interimTranscript);

  // Chart data for emotion distribution
  // Depend on totalAnalyzed (a primitive) since the statistics object is mutated in place
  const totalAnalyzed = sentiment.sentimentState?.statistics?.totalAnalyzed || 0;
  const chartData = useMemo(() => {
    const stats = sentiment.sentimentState?.statistics;
    if (!stats || totalAnalyzed === 0) return null;
    return [
      { name: 'Joy', value: Math.round(((stats.joyCount || 0) / totalAnalyzed) * 100), fill: '#FFD700' },
      { name: 'Sadness', value: Math.round(((stats.sadnessCount || 0) / totalAnalyzed) * 100), fill: '#6495ED' },
      { name: 'Anger', value: Math.round(((stats.angerCount || 0) / totalAnalyzed) * 100), fill: '#FF4500' },
      { name: 'Fear', value: Math.round(((stats.fearCount || 0) / totalAnalyzed) * 100), fill: '#9B59B6' },
      { name: 'Surprise', value: Math.round(((stats.surpriseCount || 0) / totalAnalyzed) * 100), fill: '#FF69B4' },
      { name: 'Disgust', value: Math.round(((stats.disgustCount || 0) / totalAnalyzed) * 100), fill: '#2ECC71' },
    ];
  }, [totalAnalyzed, sentiment.sentimentState?.statistics]);

  // Generate stable particle positions (max 10 for performance)
  const particles = useMemo(() =>
    Array.from({ length: 10 }, (_, i) => ({
      id: i,
      left: `${(i * 10.3 + 2) % 96 + 2}%`,
      size: 6 + (i % 3) * 3,
      duration: 16 + (i % 4) * 4,
      delay: (i * 2.5) % 14,
    })),
  []);

  // Connect sentiment to audio playback using recent emotion history (not cumulative stats)
  const currentMoodRef = useRef(currentMood);
  currentMoodRef.current = currentMood;

  useEffect(() => {
    if (!sentiment.isInitialized || !isListening) return;

    const history = sentiment.sentimentState?.history;
    if (!history || history.length === 0) return;

    // Use last 5 results to determine dominant emotion (responsive to changes)
    const recentHistory = history.slice(-5);
    const recentCounts = {};
    for (const entry of recentHistory) {
      recentCounts[entry.emotion] = (recentCounts[entry.emotion] || 0) + 1;
    }

    let dominantEmotion = 'joy';
    let maxCount = 0;
    for (const [emotion, count] of Object.entries(recentCounts)) {
      if (count > maxCount) {
        maxCount = count;
        dominantEmotion = emotion;
      }
    }

    console.log('🎯 Dominant emotion (recent):', dominantEmotion, '(count:', maxCount, '/ last', recentHistory.length, ')');
    if (dominantEmotion !== currentMoodRef.current) {
      console.log('🎵 Switching audio to dominant emotion:', dominantEmotion);
      audioService.playForEmotion(dominantEmotion);
      setCurrentMood(dominantEmotion);
      setIsPlaying(true);
    }
  }, [
    sentiment.sentimentState?.statistics?.totalAnalyzed,
    sentiment.isInitialized,
    isListening,
  ]);

  // Sync volume with audioService
  useEffect(() => {
    audioService.setVolume(volume);
  }, [volume]);

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      audioService.stop();
    };
  }, []);

  // Initialize speech recognition
  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

    if (!SpeechRecognition) {
      console.error('Speech recognition not supported');
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-US';

    recognition.onstart = () => {
      console.log('🎤 Voice recognition started');
      setIsListening(true);
    };

    recognition.onresult = (event) => {
      let interim = '';
      let final = '';

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcriptPiece = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          final += transcriptPiece + ' ';
        } else {
          interim += transcriptPiece;
        }
      }

      setInterimTranscript(interim);
      if (final) {
        setTranscript(prev => prev + final);
      }
    };

    recognition.onerror = (event) => {
      console.error('Speech recognition error:', event.error);
      if (event.error === 'not-allowed') {
        alert('Microphone access denied. Please allow microphone permissions.');
      }
      setIsListening(false);
    };

    recognition.onend = () => {
      console.log('🎤 Voice recognition ended');
      if (shouldListenRef.current) {
        console.log('🔄 Auto-restarting voice recognition...');
        try {
          recognition.start();
        } catch (e) {
          console.error('Failed to restart recognition:', e);
          setIsListening(false);
        }
      } else {
        setIsListening(false);
      }
    };

    recognitionRef.current = recognition;

    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    };
  }, []);

  const startListening = () => {
    if (recognitionRef.current && !isListening) {
      shouldListenRef.current = true;
      setTranscript('');
      setInterimTranscript('');
      recognitionRef.current.start();
    }
  };

  const stopListening = () => {
    shouldListenRef.current = false;
    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
      } catch (e) {
        // Already stopped
      }
    }
    setIsListening(false);
    setInterimTranscript('');
  };

  // Handle play/pause toggle
  const handlePlayPause = () => {
    if (isPlaying) {
      audioService.pause();
      setIsPlaying(false);
    } else {
      // If no track playing, start with current mood
      if (!audioService.currentEmotion) {
        audioService.playForEmotion(currentMood);
      } else {
        audioService.play();
      }
      setIsPlaying(true);
    }
  };

  // Handle volume change
  const handleVolumeChange = (e) => {
    const value = Number(e.target.value);
    setVolume(value);
    audioService.setVolume(value);
  };

  const musicTracks = {
    joy: { title: 'Happy Vibes', artist: 'Pre-saved Track', genre: 'Upbeat' },
    sadness: { title: 'Melancholy', artist: 'Pre-saved Track', genre: 'Slow' },
    anger: { title: 'Intense Energy', artist: 'Pre-saved Track', genre: 'Electronic' },
    fear: { title: 'Suspense', artist: 'Pre-saved Track', genre: 'Dark Ambient' },
    surprise: { title: 'Wonder', artist: 'Pre-saved Track', genre: 'Cinematic' },
    disgust: { title: 'Unsettling', artist: 'Pre-saved Track', genre: 'Eerie' },
  };

  const currentTrack = musicTracks[currentMood] || musicTracks.joy;

  return (
    <div style={{
      minHeight: '100vh',
      background: theme.background,
      padding: '32px 16px',
      transition: 'background 0.6s ease',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Ambient Particles */}
      <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 0 }}>
        {particles.map((p) => (
          <div
            key={p.id}
            style={{
              position: 'absolute',
              left: p.left,
              bottom: `-${p.size * 2}px`,
              width: `${p.size}px`,
              height: `${p.size}px`,
              borderRadius: '50%',
              background: `radial-gradient(circle at 30% 30%, ${theme.accent}, ${theme.accentSecondary}88)`,
              boxShadow: `0 0 ${p.size * 2}px ${p.size}px rgba(${theme.accentRgb}, 0.15)`,
              opacity: 0,
              animation: `floatUp ${p.duration}s ${p.delay}s ease-in-out infinite`,
              willChange: 'transform, opacity',
              transition: 'background 0.6s ease, box-shadow 0.6s ease',
            }}
          />
        ))}
      </div>

      <div style={{ maxWidth: '1400px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '32px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div style={{
              width: '56px',
              height: '56px',
              background: `linear-gradient(135deg, ${theme.accent}, ${theme.accentSecondary})`,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '20px',
              fontWeight: 'bold',
              color: 'white',
              transition: 'background 0.6s ease'
            }}>
              {user?.username?.substring(0, 2).toUpperCase() || 'SA'}
            </div>
            <div>
              <h2 style={{ color: 'white', margin: 0, fontSize: '24px', fontWeight: '600' }}>
                Welcome back
              </h2>
              <p style={{ color: theme.subtextColor, margin: 0, fontSize: '16px', transition: 'color 0.6s ease' }}>
                Ready to create your soundtrack
              </p>
            </div>
          </div>

          <button
            onClick={logout}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '12px 20px',
              background: 'rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              borderRadius: '12px',
              color: 'white',
              cursor: 'pointer',
              fontSize: '15px',
              fontWeight: '500',
              transition: 'all 0.3s'
            }}
          >
            Logout
          </button>
        </div>

        {/* Main Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '2fr 1fr',
          gap: '24px'
        }}>
          {/* Left Column */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            {/* Listening Status Card */}
            <div style={{
              background: 'rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(20px)',
              borderRadius: '24px',
              padding: '32px',
              border: '1px solid rgba(255, 255, 255, 0.2)'
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '24px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                  <div style={{
                    padding: '12px',
                    borderRadius: '50%',
                    background: isListening ? 'rgba(16, 185, 129, 0.2)' : 'rgba(107, 114, 128, 0.2)'
                  }}>
                    <span style={{ fontSize: '24px' }}>
                      {isListening ? '🎤' : '🔇'}
                    </span>
                  </div>
                  <div>
                    <h3 style={{ color: 'white', margin: '0 0 4px 0', fontSize: '18px' }}>
                      {isListening ? 'Listening to your environment' : 'Listening paused'}
                    </h3>
                    <p style={{ color: theme.subtextColor, margin: 0, fontSize: '14px', transition: 'color 0.6s ease' }}>
                      {isListening ? 'Analyzing conversation and generating music...' : 'Click to resume'}
                    </p>
                  </div>
                </div>

                <button
                  onClick={() => {
                    if (isListening) {
                      stopListening();
                      audioService.stopAll();
                      setIsPlaying(false);
                    } else {
                      audioService.stopAll();
                      startListening();
                      audioService.playIntro();
                      setIsPlaying(true);
                    }
                  }}
                  style={{
                    padding: '10px 24px',
                    borderRadius: '12px',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '15px',
                    fontWeight: '600',
                    background: isListening ? 'rgba(239, 68, 68, 0.2)' : 'rgba(16, 185, 129, 0.2)',
                    color: isListening ? '#fca5a5' : '#6ee7b7',
                    transition: 'all 0.3s'
                  }}
                >
                  {isListening ? 'Stop' : 'Start'}
                </button>
              </div>

              {/* Audio Visualizer */}
              <AudioVisualizer isActive={isListening && isPlaying} waveColors={theme.waveColors} />
            </div>

            {/* Now Playing */}
            <div style={{
              background: `linear-gradient(135deg, rgba(${theme.accentRgb}, 0.2), rgba(${theme.accentRgb}, 0.1))`,
              backdropFilter: 'blur(20px)',
              borderRadius: '24px',
              padding: '32px',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              transition: 'background 0.6s ease'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                <div style={{ position: 'relative' }}>
                  <div style={{
                    width: '80px',
                    height: '80px',
                    background: `linear-gradient(135deg, ${theme.accent}, ${theme.accentSecondary})`,
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '40px',
                    transition: 'background 0.6s ease'
                  }}>
                    {"🎵"}
                  </div>
                  {isPlaying && (
                    <div style={{
                      position: 'absolute',
                      top: '-4px',
                      right: '-4px',
                      width: '16px',
                      height: '16px',
                      background: '#10b981',
                      borderRadius: '50%',
                      border: '2px solid white',
                      animation: 'pulse 1s infinite'
                    }} />
                  )}
                </div>

                <div style={{ flex: 1 }}>
                  <h3 style={{ color: 'white', margin: '0 0 4px 0', fontSize: '18px' }}>
                    {currentTrack.title}
                  </h3>
                  <p style={{ color: theme.subtextColor, margin: '0 0 8px 0', fontSize: '14px', transition: 'color 0.6s ease' }}>
                    {currentTrack.artist} {currentMood ? `\u2022 Emotion: ${currentMood}` : ''}
                  </p>
                  <span style={{
                    display: 'inline-block',
                    padding: '4px 12px',
                    background: 'rgba(255, 255, 255, 0.1)',
                    borderRadius: '8px',
                    color: theme.subtextColor,
                    fontSize: '12px',
                    transition: 'color 0.6s ease'
                  }}>
                    {currentTrack.genre}
                  </span>
                </div>

                <div style={{ textAlign: 'right' }}>
                  <p style={{ color: theme.subtextColor, margin: '0 0 4px 0', fontSize: '14px', transition: 'color 0.6s ease' }}>
                    {isPlaying ? 'Now Playing' : 'Paused'}
                  </p>
                  <p style={{ color: 'white', margin: 0, fontSize: '14px' }}>
                    {currentMood ? `Detected: ${currentMood}` : 'Waiting for speech...'}
                  </p>
                </div>
              </div>

              {/* Progress Bar */}
              <div style={{ marginTop: '24px' }}>
                <div style={{
                  height: '8px',
                  background: 'rgba(255, 255, 255, 0.1)',
                  borderRadius: '9999px',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    height: '100%',
                    width: isPlaying ? '60%' : '0%',
                    background: `linear-gradient(to right, ${theme.accent}, ${theme.accentSecondary})`,
                    transition: 'width 0.3s, background 0.6s ease'
                  }} />
                </div>
              </div>
            </div>

            {/* Playback Control */}
            <div style={{
              background: 'rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(20px)',
              borderRadius: '24px',
              padding: '32px',
              border: '1px solid rgba(255, 255, 255, 0.2)'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                  <button
                    onClick={handlePlayPause}
                    style={{
                      width: '56px',
                      height: '56px',
                      borderRadius: '50%',
                      border: 'none',
                      background: `linear-gradient(135deg, ${theme.accent}, ${theme.accentSecondary})`,
                      cursor: 'pointer',
                      fontSize: '24px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: `0 4px 12px rgba(${theme.accentRgb}, 0.4)`,
                      transition: 'transform 0.2s, background 0.6s ease'
                    }}
                    onMouseEnter={(e) => e.target.style.transform = 'scale(1.05)'}
                    onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
                  >
                    {isPlaying ? '⏸️' : '▶️'}
                  </button>
                  <div>
                    <p style={{ color: 'white', margin: '0 0 4px 0', fontSize: '16px' }}>
                      Playback Control
                    </p>
                    <p style={{ color: theme.subtextColor, margin: 0, fontSize: '14px', transition: 'color 0.6s ease' }}>
                      {isPlaying ? 'Music is playing' : 'Music paused'}
                    </p>
                  </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <span style={{ fontSize: '20px' }}>{"🔊"}</span>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={volume}
                    onChange={handleVolumeChange}
                    style={{
                      width: '128px',
                      accentColor: theme.accent
                    }}
                  />
                  <span style={{ color: 'white', width: '48px', fontSize: '14px' }}>
                    {volume}%
                  </span>
                </div>
              </div>
            </div>

            {/* Emotion Distribution */}
            <div style={{
              background: 'rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(20px)',
              borderRadius: '24px',
              padding: '32px',
              border: '1px solid rgba(255, 255, 255, 0.2)'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                marginBottom: '24px'
              }}>
                <span style={{ fontSize: '20px' }}>{"📊"}</span>
                <h3 style={{
                  color: 'white',
                  margin: 0,
                  fontSize: '18px',
                  fontWeight: '600'
                }}>
                  Session Emotions
                </h3>
              </div>

              {!chartData ? (
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '32px 0',
                  gap: '12px'
                }}>
                  <div style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '50%',
                    background: `rgba(${theme.accentRgb}, 0.15)`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '24px'
                  }}>
                    {"🎤"}
                  </div>
                  <p style={{
                    color: theme.subtextColor,
                    fontSize: '14px',
                    margin: 0,
                    transition: 'color 0.6s ease'
                  }}>
                    Start speaking to see emotion data
                  </p>
                </div>
              ) : (
                <div>
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={chartData} barCategoryGap="20%" isAnimationActive={false}>
                      <XAxis
                        dataKey="name"
                        tick={{ fill: 'rgba(255,255,255,0.7)', fontSize: 12 }}
                        axisLine={false}
                        tickLine={false}
                      />
                      <YAxis
                        allowDecimals={false}
                        tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 11 }}
                        axisLine={false}
                        tickLine={false}
                        tickFormatter={(v) => `${v}%`}
                        width={40}
                      />
                      <Tooltip
                        isAnimationActive={false}
                        contentStyle={{
                          background: 'rgba(0,0,0,0.85)',
                          border: '1px solid rgba(255,255,255,0.1)',
                          borderRadius: '10px',
                          color: '#fff',
                          fontSize: '13px',
                          padding: '8px 14px'
                        }}
                        cursor={{ fill: 'rgba(255,255,255,0.04)' }}
                        formatter={(value) => [`${value}%`, 'Share']}
                      />
                      <Bar dataKey="value" radius={[6, 6, 0, 0]} isAnimationActive={false}>
                        {chartData.map((entry, index) => (
                          <Cell key={index} fill={entry.fill} fillOpacity={0.85} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                  <div style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '8px',
                    marginTop: '16px',
                    justifyContent: 'center'
                  }}>
                    {chartData.filter(d => d.value > 0).map((d) => (
                      <span key={d.name} style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '4px 10px',
                        background: 'rgba(255,255,255,0.06)',
                        borderRadius: '8px',
                        fontSize: '12px',
                        color: 'rgba(255,255,255,0.8)'
                      }}>
                        <span style={{
                          width: '8px',
                          height: '8px',
                          borderRadius: '50%',
                          background: d.fill,
                          display: 'inline-block'
                        }} />
                        {d.name} {d.value}%
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

          </div>

          {/* Right Column - Context Mode & Emotion Detection */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            {/* Context Mode Selector */}
            <div style={{
              background: 'rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(20px)',
              borderRadius: '24px',
              padding: '32px',
              border: '1px solid rgba(255, 255, 255, 0.2)'
            }}>
              <h3 style={{
                color: 'white',
                margin: '0 0 24px 0',
                fontSize: '20px',
                fontWeight: '600'
              }}>
                Context Mode
              </h3>

              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '12px'
              }}>
                {contextModes.map((mode) => (
                  <button
                    key={mode.id}
                    onClick={() => {
                      setSelectedMode(mode.id);
                      audioService.setMode(mode.id);
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '14px',
                      padding: '16px 18px',
                      background: selectedMode === mode.id
                        ? `rgba(${theme.accentRgb}, 0.3)`
                        : 'rgba(255, 255, 255, 0.05)',
                      border: selectedMode === mode.id
                        ? `2px solid ${theme.accent}`
                        : '1px solid rgba(255, 255, 255, 0.1)',
                      borderRadius: '14px',
                      cursor: 'pointer',
                      transition: 'all 0.3s',
                      textAlign: 'left'
                    }}
                  >
                    <span style={{ fontSize: '24px' }}>{mode.emoji}</span>
                    <span style={{
                      color: 'white',
                      fontSize: '15px',
                      fontWeight: selectedMode === mode.id ? '600' : '400'
                    }}>
                      {mode.label}
                    </span>
                    {selectedMode === mode.id && (
                      <div style={{
                        marginLeft: 'auto',
                        width: '10px',
                        height: '10px',
                        background: theme.accent,
                        borderRadius: '50%',
                        boxShadow: `0 0 8px ${theme.accent}`,
                        transition: 'background 0.6s ease'
                      }} />
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Detected Emotion Display */}
            <div style={{
              background: 'rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(20px)',
              borderRadius: '24px',
              padding: '24px',
              border: '1px solid rgba(255, 255, 255, 0.2)'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                marginBottom: '20px'
              }}>
                <span style={{ fontSize: '20px' }}>{"🎭"}</span>
                <h3 style={{
                  color: 'white',
                  margin: 0,
                  fontSize: '18px',
                  fontWeight: '600'
                }}>
                  Emotion Detection
                </h3>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <span style={{ color: theme.subtextColor, fontSize: '14px', transition: 'color 0.6s ease' }}>Current Emotion</span>
                  <span style={{
                    color: 'white',
                    fontSize: '14px',
                    padding: '4px 12px',
                    background: `rgba(${theme.accentRgb}, 0.3)`,
                    borderRadius: '8px',
                    transition: 'background 0.6s ease'
                  }}>
                    {currentMood || 'None'}
                  </span>
                </div>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <span style={{ color: theme.subtextColor, fontSize: '14px', transition: 'color 0.6s ease' }}>Confidence</span>
                  <span style={{ color: 'white', fontSize: '14px' }}>
                    {(() => {
                      const stats = sentiment.sentimentState?.statistics;
                      if (!stats || stats.totalAnalyzed === 0 || !currentMood) return '-';
                      const count = stats[`${currentMood}Count`] || 0;
                      return `${((count / stats.totalAnalyzed) * 100).toFixed(0)}%`;
                    })()}
                  </span>
                </div>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <span style={{ color: theme.subtextColor, fontSize: '14px', transition: 'color 0.6s ease' }}>Trend</span>
                  <span style={{ color: 'white', fontSize: '14px' }}>
                    {sentiment.musicData?.trend || 'stable'}
                  </span>
                </div>
              </div>
            </div>

            {/* Quick Settings */}
            <div style={{
              background: 'rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(20px)',
              borderRadius: '24px',
              padding: '24px',
              border: '1px solid rgba(255, 255, 255, 0.2)'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                marginBottom: '20px'
              }}>
                <span style={{ fontSize: '20px' }}>⚙️</span>
                <h3 style={{
                  color: 'white',
                  margin: 0,
                  fontSize: '18px',
                  fontWeight: '600'
                }}>
                  Quick Settings
                </h3>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {[
                  { label: 'Auto-adapt to mood', checked: true },
                  { label: 'Voice detection', checked: true },
                  { label: 'Ambient mode', checked: false }
                ].map((setting, index) => (
                  <label key={index} style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    cursor: 'pointer'
                  }}>
                    <span style={{ color: theme.subtextColor, fontSize: '14px', transition: 'color 0.6s ease' }}>
                      {setting.label}
                    </span>
                    <input
                      type="checkbox"
                      defaultChecked={setting.checked}
                      style={{
                        width: '20px',
                        height: '20px',
                        accentColor: theme.accent,
                        cursor: 'pointer'
                      }}
                    />
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.1); opacity: 0.8; }
        }
        @keyframes floatUp {
          0% { transform: translateY(0); opacity: 0; }
          10% { opacity: 0.4; }
          90% { opacity: 0.2; }
          100% { transform: translateY(-105vh); opacity: 0; }
        }
      `}</style>
    </div>
  );
}

export default Dashboard;
